<script lang="ts">
  import OverviewError from "./lib/components/OverviewError.svelte";
  import { overviewRequest } from "./lib/utils/overviewRequests";
  import { deviceHref } from "./lib/utils/navigation";
  import { Activity, Droplets, TriangleAlert, Zap, Server, Shield, Wrench, Home } from 'lucide-svelte';
  import { onMount, onDestroy } from 'svelte';
  import Card from './lib/components/ui/Card.svelte';
  import Metric from './lib/components/ui/Metric.svelte';
  import FlowChart from './lib/components/charts/FlowChart.svelte';
  import { user } from './lib/stores/user';
  import SystemAdmin from './SystemAdmin.svelte';
  import PortfolioOverview from './PortfolioOverview.svelte';
  import TenantDashboard from './TenantDashboard.svelte';
  import HomeownerDashboard from './lib/components/HomeownerDashboard.svelte';
  import { unitPreference, formatVolume } from './lib/stores/preferences';
  import { formatFlowAlarm } from './lib/utils/alarms';

  let overview = $state<Record<string, any> | null>(null);
  let devices = $state<any[]>([]);
  let flowData = $state<any[]>([]);
  let anomalyData = $state<any[]>([]);
  let flowRange = $state('24h');
  let selectedEUI = $state('');
  let chartDevices = $derived(devices.filter(d => deviceHref(d.device_eui)));
  let selectedDevice = $derived(chartDevices.find(d => d.device_eui.trim() === selectedEUI));
  let loading = $state(true);

  // Read user profile from store
  let currentUser = $derived($user);
  let userType = $derived(currentUser?.user_type ?? '');

  const isManagerRole = $derived(
    userType === 'property_manager' ||
    (currentUser?.properties || []).some((p: any) => p.role === 'manager') ||
    (currentUser?.customer_accounts || []).length > 0 ||
    currentUser?.role === 'manager' ||
    currentUser?.role === 'admin'
  );

  const isHomeowner = $derived(
    userType === 'customer' &&
    !isManagerRole &&
    (
      currentUser?.role === 'homeowner' ||
      currentUser?.homeowner_view ||
      currentUser?.properties?.[0]?.role === 'homeowner' ||
      (
        (currentUser?.properties?.length || 0) === 1 &&
        currentUser?.properties?.[0]?.property_type === 'single_family'
      )
    )
  );

  const portfolio = $derived(
    userType === 'property_manager' ||
    (userType === 'customer' && (
      (currentUser?.properties?.length || 0) > 1 ||
      ((currentUser?.properties?.length || 0) === 1 && isManagerRole)
    ))
  );
  let viewMode = $state<'homeowner' | 'diagnostics'>('homeowner');
  let loadError = $state('');
  let flowError = $state('');
  let flowLoading = $state(false);
  let flowRequest = 0;

  onDestroy(() => { flowRequest++; });
  let loadedForUser = '';
  $effect(() => {
    const role = currentUser?.user_type;
    if (role && role !== loadedForUser) {
      loadedForUser = role;
      if (!portfolio && !['system_admin', 'tenant'].includes(role) && !isHomeowner) {
        void loadDashboard();
      }
    }
  });

  async function loadDashboard() {
    loading = true;
    loadError = '';
    try {
      const [summary, inventory] = await Promise.all([
        overviewRequest('/api/overview', 'Overview'),
        overviewRequest('/api/devices', 'Devices')
      ]);
      overview = summary;
      devices = inventory.items || [];
      const first = devices.find(d => deviceHref(d.device_eui));
      selectedEUI = first?.device_eui.trim() || '';
      if (selectedEUI && userType !== 'manufacturer') await fetchFlowData(selectedEUI, flowRange);
    } catch (e) {
      loadError = e instanceof Error ? e.message : 'Overview unavailable. Please try again.';
    } finally {
      loading = false;
    }
  }

  function processAnomalyData(items: any[]): any[] {
    return items.map(item => item.log_type === 'hardware' ? {
      ...item,
      detected_at: item.detected_at || item.last_seen,
      status: item.status || 'reported',
      anomaly_type: item.anomaly_type || (item.flow_alarm > 0 ? 'Hardware flow alarm' : 'Hardware malfunction')
    } : item);
  }

  async function fetchFlowData(eui: string, range: string) {
    const request = ++flowRequest;
    flowData = [];
    anomalyData = [];
    flowError = '';
    flowLoading = true;
    try {
      const [flow, history] = await Promise.all([
        overviewRequest(`/api/devices/${eui}/flow?range=${range}`, 'Flow history'),
        overviewRequest(`/api/devices/${eui}/alerts/history?limit=100`, 'Alert history')
      ]);
      if (request !== flowRequest) return;
      flowData = flow.items || [];
      anomalyData = processAnomalyData(history.items || []);
    } catch (e) {
      if (request === flowRequest) flowError = e instanceof Error ? e.message : 'Flow history unavailable. Please try again.';
    } finally {
      if (request === flowRequest) flowLoading = false;
    }
  }

  async function changeRange(range: string) {
    flowRange = range;
    if (selectedEUI) await fetchFlowData(selectedEUI, range);
  }

  async function changeDevice() {
    if (selectedDevice) await fetchFlowData(selectedEUI, flowRange);
  }

  let totalFlow = $derived(
    devices.filter(d => !d.retired).length && devices.filter(d => !d.retired).every(d => typeof d.total_flow === 'number' && Number.isFinite(d.total_flow)) ? devices.filter(d => !d.retired).reduce((acc, dev) => acc + dev.total_flow, 0) : null
  );

  let activeAlerts = $derived(
    devices
      .filter(d => !d.retired && ((d.flow_alarm && d.flow_alarm > 0) || (d.malfunction_alarms && d.malfunction_alarms.length > 0)))
      .slice(0, 5)
  );

  // Dashboard heading per role
  let dashboardTitle = $derived(
    userType === 'system_admin' ? 'Fleet Operations' :
    (userType === 'customer' || userType === 'property_manager') ? 'Portfolio Overview' :
    userType === 'manufacturer' ? 'Device Provisioning' :
    userType === 'observer' ? 'Risk Dashboard' :
    'Dashboard'
  );

  let dashboardSubtitle = $derived(
    userType === 'system_admin' ? 'Platform-wide device fleet health and monitoring.' :
    (userType === 'customer' || userType === 'property_manager') ? 'Your assigned properties and devices.' :
    userType === 'manufacturer' ? 'Active device provisioning and deployment status.' :
    userType === 'observer' ? 'Anomaly and risk monitoring.' :
    'Overview of your water monitoring network.'
  );
</script>

{#if !currentUser}
  <div class="flex items-center justify-center min-h-[300px]">
    <span class="loading loading-spinner loading-md text-primary"></span>
  </div>
{:else if userType === 'system_admin'}
  <SystemAdmin />
{:else if portfolio}
  <PortfolioOverview />
{:else if userType === 'tenant'}
  <TenantDashboard />
{:else if isHomeowner && viewMode === 'homeowner'}
  <div class="space-y-3">
    <div class="flex justify-end">
      <button 
        class="btn btn-xs btn-ghost text-base-content/60 hover:text-base-content font-medium gap-1 rounded-lg"
        onclick={() => { viewMode = 'diagnostics'; void loadDashboard(); }}
      >
        Switch to Device Diagnostics View
      </button>
    </div>
    <HomeownerDashboard propertyId={currentUser?.properties?.[0]?.property_id} property={currentUser?.properties?.[0]} />
  </div>
{:else}
<div class="space-y-6">
  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
    <div>
      <h2 class="text-2xl font-bold tracking-tight">{dashboardTitle}</h2>
      <p class="text-base-content/60 mt-1 text-sm">{dashboardSubtitle}</p>
      {#if userType === 'installer'}
        <a href="#/installer/jobs" class="btn btn-sm btn-outline mt-3">Installation jobs</a>
      {/if}
    </div>
    {#if isHomeowner}
      <button 
        class="btn btn-sm btn-primary rounded-xl font-bold gap-1.5 shadow-sm self-start sm:self-auto"
        onclick={() => viewMode = 'homeowner'}
      >
        <Home class="w-4 h-4" />
        Smart Home Experience
      </button>
    {/if}
  </div>

  {#if loading && !overview}
    <div class="animate-pulse space-y-6">
      <div class="h-32 bg-base-200 rounded-xl"></div>
      <div class="h-64 bg-base-200 rounded-xl"></div>
    </div>
  {:else if loadError && !overview}
    <OverviewError message={loadError} retry={loadDashboard} />
  {:else}

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!--  MANUFACTURER: Provisioning view                           -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    {#if userType === 'manufacturer'}
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Metric 
          title="Provisioned Devices" 
          value={(overview?.provisioned_devices || 0).toString()} 
          subtitle="active in your inventory" />
        <Metric 
          title="Fleet Health" 
          value={overview?.fleet_health || 'N/A'} 
          subtitle="overall status" />
        <Metric 
          title="Company" 
          value={currentUser?.company?.company_name || 'N/A'} 
          subtitle="manufacturer account" />
      </div>

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!--  OBSERVER: Read-only risk view                             -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    {:else if userType === 'observer'}
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Metric 
          title="Monitored Devices" 
          value={(overview?.total_devices || 0).toString()} 
          subtitle="in observed portfolio" />
        <Metric 
          title="Active Anomalies" 
          value={(overview?.active_anomalies || 0).toString()} 
          trend={overview?.active_anomalies > 0 ? "Elevated" : "Normal"} 
          trendUp={overview?.active_anomalies === 0} />
        <Metric 
          title="Fleet Health" 
          value={overview?.fleet_health || 'N/A'} 
          trendUp={overview?.fleet_health === 'healthy'} />
      </div>

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!--  OWNER / TENANT: Consumption-focused view                  -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    {:else}
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Metric 
          title="Total Devices" 
          value={(overview?.total_devices || 0).toString()} 
          subtitle="registered endpoints" />
        <Metric 
          title="Active Anomalies" 
          value={(overview?.active_anomalies || 0).toString()} 
          trend={overview?.active_anomalies > 0 ? "Watch" : "Clear"} 
          trendUp={overview?.active_anomalies === 0} 
          subtitle="in the fleet" />
        <Metric 
          title="Online Nodes" 
          value={`${overview?.online_devices || 0}/${overview?.total_devices || 0}`} 
          trend={overview?.fleet_health} 
          trendUp={overview?.fleet_health === 'healthy'} />
        <Metric 
          title="Active meter totals"
          value={totalFlow == null ? "—" : formatVolume(totalFlow, $unitPreference)}
          subtitle="Reported counters; excludes retired meters" />
      </div>
    {/if}

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!--  SHARED: Flow chart + Alerts (all roles except manufacturer) -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    {#if userType !== 'manufacturer'}
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-7">
        <div class="lg:col-span-4 min-w-0 flex flex-col">
          <Card title="Flow Rate Over Time" description="Reported measurements for the selected device">
            {#if chartDevices.length}
              <div class="space-y-2 mb-3">
                <label for="overview-chart-device" class="text-xs font-medium">Chart device</label>
                <select id="overview-chart-device" class="select select-bordered select-sm w-full" bind:value={selectedEUI} onchange={changeDevice}>
                  {#each chartDevices as device}
                    <option value={device.device_eui.trim()}>{device.device_name || 'Unnamed device'} · {device.device_eui.trim()}</option>
                  {/each}
                </select>
                <a class="link text-xs" href={deviceHref(selectedEUI)}>Open selected device</a>
              </div>
            {/if}
            <div class="flex flex-wrap items-center gap-1 mt-1 mb-3">
              {#each ['24h', '7d', '30d'] as range}
                <button
                  class="px-3 py-1 text-xs font-medium rounded-md transition-colors
                    {flowRange === range ? 'bg-base-content text-base-100' : 'text-base-content/50 hover:text-base-content hover:bg-base-200'}"
                  disabled={!selectedEUI}
                  aria-pressed={flowRange === range}
                  onclick={() => changeRange(range)}
                >
                  {range}
                </button>
              {/each}
            </div>
            <div class="h-64">
              {#if !selectedEUI}
                <p class="text-sm text-base-content/70">No device with a valid identity is available for this chart.</p>
              {:else if flowLoading}
                <p role="status">Loading flow history…</p>
              {:else if flowError}
                <OverviewError message={flowError} retry={() => changeRange(flowRange)} />
              {:else if flowData.length > 0}
                <FlowChart data={flowData} anomalies={anomalyData} label={$unitPreference === 'imperial' ? "Avg Flow Rate (GPM)" : "Avg Flow Rate (L/h)"} range={flowRange} unitPreference={$unitPreference} />
              {:else}
                <div class="h-full flex items-center justify-center border-2 border-dashed border-base-200 rounded-lg text-base-content/40 text-sm">
                  No flow data available for this range
                </div>
              {/if}
            </div>
            {#if selectedEUI}<p class="text-xs text-base-content/70 mt-3">Hardware alerts mark reported events. Open the device for current status and detailed alarm history.</p>{/if}
          </Card>
        </div>
        
        <div class="lg:col-span-3 min-w-0 flex flex-col">
          <Card title="Recent Alerts" description="Issues requiring attention">
            <div class="space-y-4 mt-2">
              {#each activeAlerts as alert}
                <div class="flex items-center space-x-4 pb-3 border-b border-base-200/50 last:border-0 last:pb-0">
                  <div class="w-8 h-8 rounded-full flex items-center justify-center 
                    {alert.flow_alarm > 0 ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'}">
                    {#if alert.flow_alarm > 0}
                      <TriangleAlert class="w-4 h-4" />
                    {:else}
                      <Activity class="w-4 h-4" />
                    {/if}
                  </div>
                  <div class="flex-1 space-y-1">
                    <p class="text-sm font-medium leading-none">{alert.device_name || alert.device_eui}</p>
                    <p class="text-[11px] text-base-content/50 capitalize font-medium">
                      {#if alert.flow_alarm > 0} {formatFlowAlarm(alert.flow_alarm_flags, alert.flow_alarm)} {:else} Malfunction {/if}
                    </p>
                  </div>
                </div>
              {/each}
              {#if activeAlerts.length === 0}
                <div class="text-sm text-base-content/50 text-center py-6">No active alerts on devices.</div>
              {/if}
            </div>
          </Card>
        </div>
      </div>
    {/if}
  {/if}
</div>
{/if}
